/**
 * Product Add-ons Manager - Refactored
 * Clean architecture: each add-on block handles its own selection
 */

(function() {
  'use strict';

  // Central state
  const state = {
    basePrice: 0,
    currency: '$',
    variantPrices: {}, // { variantId: price }
    addOnBlocks: new Map() // blockId -> { selected: [{title, price}], mode: 'single'|'multi' }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  async function init() {
    const priceSummary = document.querySelector('[data-add-ons-global-summary]');
    if (!priceSummary) {
      console.log('Add-ons: No price summary found, skipping initialization');
      return;
    }

    // Parse data from Liquid-rendered attributes
    state.basePrice = parseFloat(priceSummary.dataset.basePrice) || 0;
    state.currency = priceSummary.dataset.currency || '$';
    
    // Load all variants (initial + via AJAX if needed)
    await loadAllVariants(priceSummary);

    console.log('Add-ons: Initialized with base price', state.basePrice, 'variants', Object.keys(state.variantPrices).length);

    // Initialize each add-on block independently
    initAllAddOnBlocks();
    
    // Do initial calculation
    recalculateTotal();
  }

  // Helper: Normalize price to cents (integer)
  function normalizePrice(price) {
    if (typeof price === 'number') {
      // If it's already a large number (>1000), assume it's in cents
      // Otherwise multiply by 100 to convert dollars to cents
      return price > 1000 ? Math.round(price) : Math.round(price * 100);
    }
    if (typeof price === 'string') {
      // Handle formatted strings like "665.00" -> 66500
      const clean = price.replace(/[^\d.]/g, '');
      const floatVal = parseFloat(clean);
      return isNaN(floatVal) ? 0 : Math.round(floatVal * 100);
    }
    return 0;
  }

  // Load variants: use initial batch, fetch rest via AJAX
  async function loadAllVariants(priceSummary) {
    state.variantPrices = {};

    // Load initial batch from data attribute
    let loadedCount = 0;
    try {
      const initialVariants = JSON.parse(priceSummary.dataset.initialVariants || '[]');
      initialVariants.forEach(v => {
        state.variantPrices[v.id] = normalizePrice(v.price);
      });
      loadedCount = initialVariants.length;
      console.log('Add-ons: Loaded initial batch', loadedCount);
    } catch (e) {
      console.error('Add-ons: Failed to parse initial variants', e);
    }

    // Fetch all variants via product.json (Shopify returns 250 per page)
    const productHandle = priceSummary.dataset.productHandle;
    const totalVariants = parseInt(priceSummary.dataset.totalVariants) || 0;

    if (productHandle && totalVariants > 0) {
      const pageSize = 250;
      const pagesNeeded = Math.ceil(totalVariants / pageSize);
      const pageNumbers = Array.from({ length: pagesNeeded }, (_, i) => i + 1); // Start from page 1

      console.log(`Add-ons: Fetching ${pagesNeeded} page(s) for ${totalVariants} total variants`);

      const fetchPromises = pageNumbers.map(async (page) => {
        try {
          const response = await fetch(`/products/${productHandle}.json?page=${page}`);
          if (!response.ok) {
            console.error(`Add-ons: Failed to fetch page ${page}`, response.status);
            return [];
          }
          const data = await response.json();
          return data.product?.variants || [];
        } catch (e) {
          console.error(`Add-ons: Error fetching page ${page}`, e);
          return [];
        }
      });

      const results = await Promise.all(fetchPromises);
      let fetchedCount = 0;

      results.forEach(variants => {
        variants.forEach(v => {
          state.variantPrices[v.id] = normalizePrice(v.price);
          fetchedCount++;
        });
      });

      console.log('Add-ons: Loaded variants via AJAX', fetchedCount);
    }

    console.log('Add-ons: Total variants loaded', Object.keys(state.variantPrices).length);
  }

  // Initialize all add-on blocks found on the page
  function initAllAddOnBlocks() {
    const blocks = document.querySelectorAll('[data-block-type="add-ons"]');
    
    blocks.forEach(block => {
      const blockId = block.dataset.blockId;
      const selectionMode = block.dataset.selectionMode || 'single';
      const itemsToShow = parseInt(block.dataset.itemsToShow) || 3;
      const validCount = parseInt(block.dataset.validCount) || 0;
      const defaultSelected = block.dataset.defaultSelected || 'none';
      
      // Initialize block state
      state.addOnBlocks.set(blockId, {
        selected: [], // Array of { title, price }
        mode: selectionMode
      });
      
      // Initialize toggle button if needed
      if (validCount > itemsToShow) {
        initToggleButton(block, itemsToShow);
      }
      
      // Initialize this block's radio group
      initBlockRadioGroup(block, blockId, selectionMode);
      
      // Apply default selection if specified
      if (defaultSelected !== 'none') {
        applyDefaultSelection(block, blockId, defaultSelected, selectionMode);
      }
    });
  }

  // Initialize a single block's radio inputs
  function initBlockRadioGroup(block, blockId, selectionMode) {
    const inputs = block.querySelectorAll('.add-ons__item-input');
    const blockState = state.addOnBlocks.get(blockId);
    
    inputs.forEach(input => {
      // Check for pre-selected on page load
      if (input.checked) {
        const title = input.dataset.addOnTitle;
        const price = parseFloat(input.dataset.addOnPrice) || 0;
        if (title && price > 0) {
          blockState.selected.push({ title, price });
        }
      }
      
      // Add change listener
      input.addEventListener('change', function() {
        handleBlockSelectionChange(blockId, this, selectionMode);
      });
    });
  }

  // Apply default selection to a block
  function applyDefaultSelection(block, blockId, itemNumber, selectionMode) {
    const inputs = block.querySelectorAll('.add-ons__item-input');
    let targetInput = null;
    
    // Find the input with matching item number
    inputs.forEach(input => {
      if (input.dataset.addOnItemNumber === itemNumber) {
        targetInput = input;
      }
    });
    
    if (targetInput) {
      // Check the input and trigger change event
      targetInput.checked = true;
      targetInput.dispatchEvent(new Event('change', { bubbles: true }));
      console.log('Add-ons: Applied default selection to item', itemNumber);
    } else {
      console.warn('Add-ons: Could not find item', itemNumber, 'for default selection');
    }
  }

  // Handle selection change within a specific block
  function handleBlockSelectionChange(blockId, input, selectionMode) {
    const blockState = state.addOnBlocks.get(blockId);
    const title = input.dataset.addOnTitle;
    const price = parseFloat(input.dataset.addOnPrice) || 0;
    
    if (selectionMode === 'single') {
      // Single selection: clear all, add new
      blockState.selected = input.checked ? [{ title, price }] : [];
    } else {
      // Multi selection: add/remove from array
      if (input.checked) {
        blockState.selected.push({ title, price });
      } else {
        blockState.selected = blockState.selected.filter(item => item.title !== title);
      }
    }
    
    // Recalculate total and update variant
    recalculateTotal();
  }

  // Calculate total from all blocks and update everything
  function recalculateTotal() {
    // Sum all add-ons from all blocks
    let addOnsTotal = 0;
    state.addOnBlocks.forEach((blockState) => {
      blockState.selected.forEach(item => {
        addOnsTotal += item.price;
      });
    });
    
    // Calculate grand total (NUMERIC addition, not string concat!)
    const grandTotal = state.basePrice + addOnsTotal;
    
    console.log('Add-ons: Base', state.basePrice, '+ Add-ons', addOnsTotal, '= Total', grandTotal);
    
    // Update price display
    updatePriceDisplay(grandTotal, addOnsTotal);
    
    // Find and select matching variant
    selectVariantByPrice(grandTotal);
  }

  function initToggleButton(block, itemsToShow) {
    const toggleBtn = block.querySelector('.add-ons__toggle');
    if (!toggleBtn) return;

    const hiddenItems = block.querySelectorAll('.add-ons__item[data-add-on-index]');
    const showText = toggleBtn.querySelector('.add-ons__toggle-text-show');
    const hideText = toggleBtn.querySelector('.add-ons__toggle-text-hide');

    toggleBtn.addEventListener('click', function() {
      const isExpanded = this.dataset.expanded === 'true';
      
      hiddenItems.forEach((item) => {
        const itemIndex = parseInt(item.dataset.addOnIndex);
        if (itemIndex > itemsToShow) {
          item.classList.toggle('hidden', isExpanded);
        }
      });

      if (isExpanded) {
        this.dataset.expanded = 'false';
        if (showText) showText.style.display = '';
        if (hideText) hideText.style.display = 'none';
      } else {
        this.dataset.expanded = 'true';
        if (showText) showText.style.display = 'none';
        if (hideText) hideText.style.display = '';
      }
    });
  }

  // Update price display in global summary
  function updatePriceDisplay(grandTotal, addOnsTotal) {
    const globalSummary = document.querySelector('[data-add-ons-global-summary]');
    if (!globalSummary) return;
    
    const totalEl = globalSummary.querySelector('[data-add-ons-total]');
    const addOnsEl = globalSummary.querySelector('[data-add-ons-amount]');
    
    if (totalEl) totalEl.textContent = formatPrice(grandTotal);
    if (addOnsEl) {
      if (addOnsTotal > 0) {
        addOnsEl.textContent = `+${formatPrice(addOnsTotal)}`;
        addOnsEl.style.display = 'inline-block';
        globalSummary.classList.add('has-addons');
      } else {
        addOnsEl.textContent = '';
        addOnsEl.style.display = 'none';
        globalSummary.classList.remove('has-addons');
      }
    }
  }

  // Find and select variant matching target price
  function selectVariantByPrice(targetPrice) {
    // Use loaded variant prices from state
    const priceMap = state.variantPrices;
    if (!priceMap || Object.keys(priceMap).length === 0) {
      console.log('Add-ons: No variant prices loaded yet');
      return;
    }
    
    // Find best matching variant
    let bestVariantId = null;
    let bestDiff = Infinity;
    
    Object.entries(priceMap).forEach(([variantId, price]) => {
      const variantPrice = parseFloat(price);
      if (!isNaN(variantPrice)) {
        const diff = Math.abs(variantPrice - targetPrice);
        if (diff < bestDiff && diff <= 100) { // Within 100 cents (1 unit) tolerance
          bestDiff = diff;
          bestVariantId = variantId;
        }
      }
    });
    
    if (bestVariantId) {
      console.log('Add-ons: Selecting variant', bestVariantId, 'for price', targetPrice);
      
      // Update ONLY the hidden variant input - no dependency on UI selectors
      const variantInput = document.querySelector('input[name="id"].product-variant-id');
      if (variantInput) {
        variantInput.value = bestVariantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        console.log('Add-ons: Updated variant input to', bestVariantId);
      }
    } else {
      console.log('Add-ons: No matching variant found for price', targetPrice);
    }
  }

  // Helper: Format price for display (amount is in cents)
  function formatPrice(amount) {
    const dollars = amount / 100;
    const formatted = dollars.toFixed(2).replace('.', ',');
    return `${formatted} ${state.currency}`;
  }

  // Public API
  window.ProductAddOns = {
    state: state,
    recalculate: recalculateTotal
  };

})();
